import {
  Component,
  Show,
  createSignal,
  createEffect,
  onCleanup,
} from "solid-js";

export interface ScrollToTopButtonProps {
  /** Scroll container to watch; clicking the button returns it to the top. */
  container: HTMLElement | undefined;
  /** Scroll distance in pixels before the button appears. Defaults to 300. */
  threshold?: number;
}

/**
 * Floating action button that returns a scroll container to the top.
 *
 * Hidden until the container is scrolled past `threshold`, so it costs nothing
 * on short pages. Positioned absolutely — the parent needs `relative`.
 *
 * Anchored bottom-LEFT: the bottom-right corner is already claimed by two
 * fixed, higher-stacked overlays — the toast stack (ToastContext) and
 * UpdateNotification — and either one covers a button placed there, leaving it
 * unclickable while it is on screen.
 */
export const ScrollToTopButton: Component<ScrollToTopButtonProps> = (props) => {
  const [isVisible, setIsVisible] = createSignal(false);

  createEffect(() => {
    const container = props.container;
    if (!container) {
      setIsVisible(false);
      return;
    }

    const threshold = props.threshold ?? 300;
    const update = () => setIsVisible(container.scrollTop > threshold);

    update();
    container.addEventListener("scroll", update, { passive: true });
    onCleanup(() => container.removeEventListener("scroll", update));
  });

  const handleClick = () => {
    const container = props.container;
    if (!container) return;

    const reducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    container.scrollTo({ top: 0, behavior: reducedMotion ? "auto" : "smooth" });
  };

  return (
    <Show when={isVisible()}>
      <button
        type="button"
        onClick={handleClick}
        aria-label="Scroll to top"
        title="Scroll to top"
        class="absolute bottom-6 left-6 z-10 p-3 rounded-full bg-surface text-ink-2 border border-line transition-colors hover:bg-sunken hover:text-ink focus-ring"
      >
        <svg
          class="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M5 10l7-7m0 0l7 7m-7-7v18"
          />
        </svg>
      </button>
    </Show>
  );
};
